import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useCallback, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { ClearFiltersHint } from '~components/Filters/ClearFiltersHint';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { Order } from '~hooks/useOrders/models';

import { ORDERS_LIST_GAP } from '../../constants';
import OrdersListCard from './OrdersListCard';

interface OrdersListProps {
  editedOrderId?: string;
  hasMore: boolean;
  hasUnfilteredResults?: boolean;
  isLoading: boolean;
  onCancelOrder: (order: Order) => void;
  onClearFilters?: () => void;
  onCloneOrder: (order: Order) => void;
  onCopyAndEditOrder: (order: Order) => void;
  onDownloadFiles: (jobIds: string[]) => void;
  onDownloadJobCardPdfs: (
    jobIds: string[],
    recipientType: JobCardPdfRecipientType,
  ) => void;
  onEditOrder: (order: Order) => void;
  onLoadMore: () => void;
  onTextAllDrivers: (order: Order) => void;
  orders: Order[];
}

export default function OrdersList({
  editedOrderId,
  hasMore,
  hasUnfilteredResults,
  isLoading,
  onCancelOrder,
  onClearFilters,
  onCloneOrder,
  onCopyAndEditOrder,
  onDownloadFiles,
  onDownloadJobCardPdfs,
  onEditOrder,
  onLoadMore,
  onTextAllDrivers,
  orders,
}: OrdersListProps) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const itemCount = orders.length + 1;

  const handleEndReached = useCallback(() => {
    if (hasMore && !isLoading) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const renderOrderItem = useCallback(
    (index: number) => {
      if (index >= orders.length) {
        if (isLoading) {
          return <VirtualizedOrdersLoadingIndicator />;
        }

        const showClearFiltersHint = orders.length === 0 && Boolean(hasUnfilteredResults);

        return (
          <AllOrdersLoadedIndicator
            hasOrders={orders.length > 0}
            onClearFilters={onClearFilters}
            showClearFiltersHint={showClearFiltersHint}
          />
        );
      }

      const order = orders[index];

      return (
        <Box sx={{ pt: ORDERS_LIST_GAP, px: 6 }}>
          <OrdersListCard
            key={order.orderId}
            isEditInProgress={order.id === editedOrderId}
            onCancelOrder={onCancelOrder}
            onCloneOrder={onCloneOrder}
            onCopyAndEditOrder={onCopyAndEditOrder}
            onDownloadFiles={onDownloadFiles}
            onDownloadJobCardPdfs={onDownloadJobCardPdfs}
            onEditOrder={onEditOrder}
            onTextAllDrivers={onTextAllDrivers}
            order={order}
          />
        </Box>
      );
    },
    [
      orders,
      isLoading,
      hasUnfilteredResults,
      onCancelOrder,
      onClearFilters,
      onCloneOrder,
      onCopyAndEditOrder,
      onDownloadFiles,
      onDownloadJobCardPdfs,
      onEditOrder,
      onTextAllDrivers,
      editedOrderId,
    ],
  );

  return (
    <Virtuoso
      ref={virtuosoRef}
      data={Array.from({ length: itemCount }, (_, index) => index)}
      endReached={handleEndReached}
      itemContent={renderOrderItem}
      overscan={5}
      style={{ height: '100%' }}
    />
  );
}

export function VirtualizedOrdersLoadingIndicator() {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: 30,
        justifyContent: 'center',
        mt: 1.5,
      }}
    >
      <LoadingSpinner isVisible />
    </Box>
  );
}

export function AllOrdersLoadedIndicator({
  hasOrders,
  onClearFilters,
  showClearFiltersHint,
}: {
  hasOrders: boolean;
  onClearFilters?: () => void;
  showClearFiltersHint: boolean;
}) {
  return (
    <Box sx={{ px: 6, pb: ORDERS_LIST_GAP, pt: 1.5 }}>
      <DividerWithText
        text={
          hasOrders ? t('dispatch.dispatch_v2.all_orders_loaded') : t('common.no_data')
        }
      />

      {showClearFiltersHint && <ClearFiltersHint onClearFilters={onClearFilters} />}
    </Box>
  );
}
